import { Link } from 'react-router-dom';
import LegalPage from './LegalPage';

// DRAFT. Have counsel review before publishing, and resolve every <Fill>.
function AcceptableUsePolicy() {
  return (
    <LegalPage
      title="Acceptable Use Policy"
      lead="The rules for using the InfoRight applications safely, lawfully, and fairly."
      effectiveDate="September 30, 2026"
    >
      <section>
        <h2>1. Scope</h2>
        <p>
          This Acceptable Use Policy ("Policy") applies to everyone who accesses or uses the
          InfoRight subscription service, including the InfoRight web and mobile applications,
          integrations, APIs, and related documentation (the "Service"). That includes our
          customers ("Customer", "you") and the employees, contractors, field crews, site visitors,
          and other individuals a Customer authorizes to use the Service ("Authorized Users").
        </p>
        <p>
          This Policy forms part of the{' '}
          <Link to="/legal/subscription-agreement">InfoRight SaaS Subscription Agreement</Link>{' '}
          (the "Agreement") between InfoRight, Inc. ("InfoRight", "we", "us") and each Customer.
          Capitalized terms not defined here have the meanings given in the Agreement. If this
          Policy and the Agreement conflict, the Agreement controls.
        </p>
        <p>
          Customers are responsible for their Authorized Users' compliance with this Policy.
        </p>
      </section>

      <section>
        <h2>2. Prohibited uses</h2>
        <p>You and your Authorized Users may not use the Service to:</p>
        <ul>
          <li>
            Violate any applicable law or regulation, including mine safety, environmental,
            employment, export control, and privacy laws.
          </li>
          <li>
            Enter, store, or submit records you know to be false or misleading, including falsified
            inspection results, training records, certifications, or visitor approvals.
          </li>
          <li>
            Infringe or misappropriate anyone's intellectual property, privacy, or other rights.
          </li>
          <li>
            Upload or transmit viruses, malware, or other code designed to harm, disrupt, or gain
            unauthorized access to any system or data.
          </li>
          <li>
            Harass, threaten, or discriminate against any person, or store content that is
            unlawful, defamatory, or obscene.
          </li>
          <li>Send spam or unsolicited messages through the Service's notification features.</li>
        </ul>
      </section>

      <section>
        <h2>3. Security and system integrity</h2>
        <p>You may not:</p>
        <ul>
          <li>
            Access or attempt to access any account, data, or part of the Service you are not
            authorized to use, including another customer's data.
          </li>
          <li>
            Probe, scan, or test the vulnerability of the Service, or bypass any security or
            authentication measure, without our prior written consent.
          </li>
          <li>
            Interfere with or disrupt the Service or place an unreasonable load on it, including
            through automated scripts, bots, or scraping, except through interfaces and integrations
            we provide or approve.
          </li>
          <li>
            Reverse engineer, decompile, or disassemble the Service, except to the extent applicable
            law expressly allows this despite this restriction.
          </li>
          <li>
            Connect the Service to any third-party system, database, or file you do not have the
            right to access and share.
          </li>
        </ul>
      </section>

      <section>
        <h2>4. Accounts and credentials</h2>
        <ul>
          <li>
            Each Authorized User must use their own login. Credentials may not be shared, and one
            account may not be used by more than one person.
          </li>
          <li>
            Keep credentials and mobile devices used with the Service secure. Tell us promptly at{' '}
            <a href="mailto:inforight@inforight.com">inforight@inforight.com</a> if you suspect an
            account has been compromised.
          </li>
          <li>
            Customers must remove access promptly for Authorized Users who leave the organization or
            no longer need it.
          </li>
        </ul>
      </section>

      <section>
        <h2>5. Subscription scope</h2>
        <p>
          Your subscription covers the applications and sites set out in your order. You may not
          use the Service for sites, entities, or applications outside that scope, or resell,
          sublicense, rent, or provide the Service to third parties, unless your order or the
          Agreement allows it. "Unlimited field users" means unlimited Authorized Users at your
          subscribed sites. It does not permit sharing access with unaffiliated organizations.
        </p>
      </section>

      <section>
        <h2>6. Sensitive data</h2>
        <p>
          The Service is designed for operational, inspection, training, and visitor records.
          Unless we have agreed otherwise in writing, do not submit:
        </p>
        <ul>
          <li>Social Security numbers or other government identification numbers;</li>
          <li>payment card or bank account numbers;</li>
          <li>medical or health information, beyond what a training or certification record needs;</li>
          <li>information about children under 16.</li>
        </ul>
        <p>
          You are responsible for having the notices, consents, and legal basis needed to submit
          any personal information about your Authorized Users and visitors to the Service.
        </p>
      </section>

      <section>
        <h2>7. Safety and compliance responsibilities</h2>
        <p>
          The Service helps you record, route, and retrieve safety and compliance information. It
          does not replace your safety program, your obligations under the Federal Mine Safety and
          Health Act, MSHA regulations, or other laws, or the judgment of qualified personnel. In
          particular:
        </p>
        <ul>
          <li>
            Do not rely only on an automated notification to make sure a defect is addressed. Your
            normal lockout, tag-out, and removal-from-service procedures still apply.
          </li>
          <li>
            You remain responsible for the accuracy of the records your Authorized Users enter and
            for confirming that training and certifications meet regulatory requirements.
          </li>
          <li>
            The Service can be unavailable, for example when a device has no connectivity and the
            application does not have an offline mode. Keep a fallback process for safety-critical
            steps.
          </li>
        </ul>
      </section>

      <section>
        <h2>8. Reporting violations and vulnerabilities</h2>
        <p>
          If you become aware of a violation of this Policy, or believe you have found a security
          vulnerability in the Service, please report it to{' '}
          <a href="mailto:inforight@inforight.com">inforight@inforight.com</a>. We ask security researchers to give us a reasonable time to investigate and fix an issue
          before disclosing it, and not to access or change data that isn't theirs.
        </p>
      </section>

      <section>
        <h2>9. Enforcement</h2>
        <p>
          We may investigate suspected violations of this Policy. If we reasonably believe a
          violation has occurred, we may take proportionate action as the Agreement allows. This can
          include removing content, suspending affected users or accounts, or suspending the
          Service. Where practical, we will notify you first and give you a chance to fix the issue.
          We may act immediately where needed to prevent harm to the Service, other customers, or
          any person, or to comply with law. We may report suspected illegal activity to law
          enforcement and cooperate with lawful requests.
        </p>
      </section>

      <section>
        <h2>10. Changes to this Policy</h2>
        <p>
          We may update this Policy from time to time. We will post the updated version on this
          page with a new effective date and, for material changes, notify Customer administrators
          by email at least 30 days before the changes take effect.
        </p>
      </section>

      <section>
        <h2>11. Contact</h2>
        <p>
          Questions about this Policy can be sent to{' '}
          <a href="mailto:support@inforight.com">support@inforight.com</a> or to InfoRight,
          Inc., 8977 South 1300 West #2001, West Jordan, UT 84088.
        </p>
      </section>
    </LegalPage>
  );
}

export default AcceptableUsePolicy;
